/**
 * Utilities para formatação de números e prevenção de floating point errors
 */

/**
 * Remove erros de floating point em números decimais
 * 
 * @example
 * cleanFloatingPoint(3.500000000000004) // 3.5
 * cleanFloatingPoint(7.000000000000001) // 7
 * cleanFloatingPoint(0.1 + 0.2) // 0.3
 */
export function cleanFloatingPoint(value: number, decimals: number = 10): number {
  return Number(value.toFixed(decimals));
}

/**
 * Formata percentual removendo floating point errors
 * 
 * @param value - Valor decimal (0.035 = 3.5%)
 * @param decimals - Casas decimais (default: 1)
 * @returns String formatada com %
 * 
 * @example
 * formatPercent(0.035) // "3.5%"
 * formatPercent(0.0350000000004) // "3.5%"
 * formatPercent(0.23456, 2) // "23.46%"
 */
export function formatPercent(value: number, decimals: number = 1): string {
  const cleaned = cleanFloatingPoint(value * 100, decimals + 2);
  return `${cleaned.toFixed(decimals)}%`;
}

/**
 * Formata moeda brasileira (R$)
 * 
 * @param value - Valor numérico
 * @param decimals - Casas decimais (default: 2)
 * @returns String formatada R$ X.XXX,XX
 * 
 * @example
 * formatCurrency(1234567.89) // "R$ 1.234.567,89"
 * formatCurrency(1000000, 0) // "R$ 1.000.000"
 */
export function formatCurrency(value: number, decimals: number = 2): string {
  const cleaned = cleanFloatingPoint(value, decimals);
  return cleaned.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formata número removendo floating point errors
 * 
 * @param value - Valor numérico
 * @param decimals - Casas decimais (default: 2)
 * @returns Número limpo
 * 
 * @example
 * formatNumber(3.500000000000004, 1) // 3.5
 * formatNumber(3.500000000000004, 4) // 3.5000
 */
export function formatNumber(value: number, decimals: number = 2): number {
  return cleanFloatingPoint(value, decimals);
}

/**
 * Formata input de percentual (para campos de formulário)
 * Remove floating point errors e converte para string
 * 
 * @param value - Valor decimal (0.035 = 3.5%)
 * @param decimals - Casas decimais (default: 4 para precisão em inputs)
 * @returns String com valor percentual limpo
 * 
 * @example
 * formatPercentInput(0.035) // "3.5"
 * formatPercentInput(0.0350000000004) // "3.5"
 */
export function formatPercentInput(value: number, decimals: number = 4): string {
  const percentValue = value * 100;
  const cleaned = cleanFloatingPoint(percentValue, decimals);
  // Remove trailing zeros desnecessários
  return cleaned.toString().replace(/\.?0+$/, '');
}
